<template>
  <div
    class="social-connections-list"
    :class="{
      'social-connections-list--compact': compact,
      'social-connections-list--unified': unified,
      'social-connections-list--paired-grid': compact && unified,
    }"
  >
    <div v-if="unified" class="social-connections-header">
      <span class="social-connections-header-title">
        {{ tt("userProfile.socialSectionTitle", "Redes sociales") }}
      </span>
    </div>

    <div
      v-for="platform in standardPlatforms"
      :key="platform.name"
      class="social-platform-card"
      :class="{
        'card momo-card-dark': !unified,
        'social-platform-card-disabled': platform.comingSoon,
        'social-platform-card-connected': platform.connected && !platform.comingSoon,
      }"
      role="button"
      :tabindex="platform.comingSoon ? -1 : 0"
      @click="handleCardClick(platform)"
      @keydown.enter.prevent="handleCardClick(platform)"
      @keydown.space.prevent="handleCardClick(platform)"
    >
      <div class="social-platform-card-body">
        <div class="social-platform-row">
          <div class="social-platform-left">
            <img
              :src="platform.logoUrl"
              :alt="`${platform.label} logo`"
              class="social-logo"
            />
            <div class="social-platform-info">
              <div class="social-platform-name">{{ platform.label }}</div>
              <div
                v-if="platformSecondaryText(platform)"
                class="social-platform-handle"
              >
                {{ platformSecondaryText(platform) }}
              </div>
            </div>
          </div>
          <div class="social-platform-right">
            <span
              class="social-status-badge"
              :class="platformStatusBadgeClass(platform)"
            >
              {{ platformStatusLabel(platform) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="facebookPlatform"
      class="social-platform-card social-platform-card--facebook-row"
      :class="{
        'card momo-card-dark': !unified,
        'social-platform-card-disabled': facebookPlatform.comingSoon,
        'social-platform-card-connected': facebookPlatform.connected && !facebookPlatform.comingSoon,
      }"
      role="button"
      :tabindex="facebookPlatform.comingSoon ? -1 : 0"
      @click="handleCardClick(facebookPlatform)"
      @keydown.enter.prevent="handleCardClick(facebookPlatform)"
      @keydown.space.prevent="handleCardClick(facebookPlatform)"
    >
      <div class="social-platform-card-body">
        <template v-if="unified">
          <div class="social-platform-row">
            <div class="social-platform-left">
              <img
                :src="facebookPlatform.logoUrl"
                :alt="`${facebookPlatform.label} logo`"
                class="social-logo"
              />
              <div class="social-platform-info">
                <div class="social-platform-name">{{ facebookPlatform.label }}</div>
                <div
                  v-if="platformSecondaryText(facebookPlatform)"
                  class="social-platform-handle"
                >
                  {{ platformSecondaryText(facebookPlatform) }}
                </div>
              </div>
            </div>
            <div class="social-platform-right">
              <span
                class="social-status-badge"
                :class="platformStatusBadgeClass(facebookPlatform)"
              >
                {{ platformStatusLabel(facebookPlatform) }}
              </span>
            </div>
          </div>
        </template>

        <div v-else class="facebook-connection-panel">
          <div
            class="social-platform-left social-platform-left--clickable facebook-connection-identity"
            role="button"
            :tabindex="facebookPlatform.comingSoon ? -1 : 0"
            @click.stop="handleFacebookLeftClick(facebookPlatform)"
            @keydown.enter.stop.prevent="handleFacebookLeftClick(facebookPlatform)"
            @keydown.space.stop.prevent="handleFacebookLeftClick(facebookPlatform)"
          >
            <img
              :src="facebookPlatform.logoUrl"
              :alt="`${facebookPlatform.label} logo`"
              class="social-logo"
            />
            <div class="social-platform-info">
              <div class="social-platform-name">{{ facebookPlatform.label }}</div>
              <div
                v-if="platformSecondaryText(facebookPlatform)"
                class="social-platform-handle"
              >
                {{ platformSecondaryText(facebookPlatform) }}
              </div>
              <span
                v-else
                class="social-status-badge social-status-badge--inline"
                :class="platformStatusBadgeClass(facebookPlatform)"
              >
                {{ platformStatusLabel(facebookPlatform) }}
              </span>
            </div>
          </div>

          <div class="facebook-page-settings">
            <label for="facebook-page-id-input" class="facebook-page-settings-label">
              {{ tt("userProfile.facebookPageIdLabel", "Página de Facebook") }}
            </label>
            <div class="facebook-page-settings-controls">
              <select
                id="facebook-page-id-input"
                v-model="facebookPageIdDraft"
                class="form-control facebook-page-id-input facebook-page-id-input--inline"
                :disabled="facebookPageSelectDisabled"
                @click.stop
              >
                <option value="" disabled>
                  {{ facebookPageSelectPlaceholder }}
                </option>
                <option
                  v-for="page in facebookPageOptions"
                  :key="page.id"
                  :value="page.id"
                >
                  {{ page.name }}
                </option>
              </select>
              <button
                type="button"
                class="btn btn-primary btn-sm facebook-page-id-save facebook-page-id-save--inline"
                :disabled="facebookPageSaveDisabled"
                @click.stop="saveFacebookPageId"
              >
                {{ facebookPageIdSubmitLabel }}
              </button>
            </div>
            <p
              v-if="isFacebookConnected && facebookPagesLoading"
              class="facebook-page-id-feedback facebook-page-id-feedback--inline mb-0"
            >
              {{ tt("userProfile.loadingFacebookPages", "Cargando páginas de Facebook...") }}
            </p>
            <p
              v-else-if="isFacebookConnected && facebookPagesLoadError"
              class="facebook-page-id-feedback facebook-page-id-feedback--inline facebook-page-id-feedback-error mb-0"
            >
              {{ tt("userProfile.facebookPagesLoadError", "No se pudieron cargar tus páginas de Facebook.") }}
            </p>
            <p
              v-else-if="isFacebookConnected && !facebookPageOptions.length"
              class="facebook-page-id-feedback facebook-page-id-feedback--inline mb-0"
            >
              {{ tt("userProfile.facebookPagesEmpty", "No hay páginas de Facebook disponibles para esta cuenta.") }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="facebookPlatform && unified"
      class="social-platform-card social-platform-card--facebook-page social-platform-card-static"
      role="region"
      :aria-label="tt('userProfile.facebookPageIdRegion', 'Configuración de Facebook Page ID')"
    >
      <div class="social-platform-card-body social-platform-card-body--facebook-page">
        <div class="facebook-page-select-field">
          <img
            :src="facebookPageIconUrl"
            alt=""
            class="facebook-page-select-icon"
            aria-hidden="true"
          />
          <select
            id="facebook-page-id-input"
            v-model="facebookPageIdDraft"
            class="form-control border-input facebook-page-id-select facebook-page-id-select--solo"
            :disabled="facebookPageSelectDisabled"
            :aria-label="facebookPageSelectAriaLabel"
            @change="onFacebookPageDraftChange"
          >
            <option value="" disabled>
              {{ facebookPageSelectPlaceholder }}
            </option>
            <option
              v-for="page in facebookPageOptions"
              :key="page.id"
              :value="page.id"
            >
              {{ page.name }}
            </option>
          </select>
        </div>
        <p
          v-if="isFacebookConnected && facebookPagesLoading"
          class="facebook-page-id-feedback facebook-page-id-feedback--solo mb-0"
        >
          {{ tt("userProfile.loadingFacebookPages", "Cargando páginas de Facebook...") }}
        </p>
        <p
          v-else-if="isFacebookConnected && facebookPagesLoadError"
          class="facebook-page-id-feedback facebook-page-id-feedback--solo facebook-page-id-feedback-error mb-0"
        >
          {{ tt("userProfile.facebookPagesLoadError", "No se pudieron cargar tus páginas de Facebook.") }}
        </p>
        <p
          v-else-if="isFacebookConnected && !facebookPageOptions.length"
          class="facebook-page-id-feedback facebook-page-id-feedback--solo mb-0"
        >
          {{ tt("userProfile.facebookPagesEmpty", "No hay páginas de Facebook disponibles para esta cuenta.") }}
        </p>
      </div>
    </div>
    <schedule-subscription-prompt-modal
      v-model="showScheduleSubscriptionPromptModal"
      :has-active-plan="scheduleSubscriptionHasActivePlan"
      @close="closeScheduleSubscriptionPromptModal"
      @subscribe="openSubscriptionFromSchedulePrompt"
    />
  </div>
</template>
<script>
import { supabase } from "@/supabase";
import { buildAuthHeaders, isImpersonating } from "@/utils/impersonation";
import ScheduleSubscriptionPromptModal from "@/components/common/ScheduleSubscriptionPromptModal.vue";
import { useCurrentUserStore } from "@/stores/currentUser";
import { useSocialProfileStore } from "@/stores/socialProfile";
import { useFacebookPageIdStore } from "@/stores/facebookPageId";
import facebookPageIcon from "@/assets/img/platform-icons/facebook.svg";

export default {
  components: {
    ScheduleSubscriptionPromptModal,
  },
  props: {
    compact: {
      type: Boolean,
      default: false,
    },
    unified: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      facebookPageIconUrl: facebookPageIcon,
      platforms: [
        {
          name: "instagram",
          label: "Instagram",
          logoUrl: "https://img.icons8.com/fluency/96/instagram-new.png",
          connected: false,
          username: "",
          comingSoon: false,
        },
        {
          name: "tiktok",
          label: "TikTok",
          logoUrl: "https://img.icons8.com/color/96/tiktok--v1.png",
          connected: false,
          username: "",
          comingSoon: false,
        },
        {
          name: "youtube",
          label: "YouTube",
          logoUrl: "https://img.icons8.com/color/96/youtube-play.png",
          connected: false,
          username: "",
          comingSoon: false,
        },
        {
          name: "x",
          label: "X",
          logoUrl: "https://img.icons8.com/fluency/96/twitterx.png",
          connected: false,
          username: "",
          comingSoon: false,
        },
        {
          name: "facebook",
          label: "Facebook",
          logoUrl: "https://img.icons8.com/fluency/96/facebook-new.png",
          connected: false,
          username: "",
          comingSoon: false,
        },
      ],
      loading: false,
      connectablePlatforms: ["instagram", "tiktok", "youtube", "x", "facebook"],
      facebookPageId: "",
      facebookPageIdDraft: "",
      facebookPageIdSaving: false,
      facebookPages: [],
      facebookPagesLoading: false,
      facebookPagesLoadError: "",
      facebookPagesRequestedForCurrentConnection: false,
      initialConnectionsLoadReported: false,
      showScheduleSubscriptionPromptModal: false,
      scheduleSubscriptionHasActivePlan: false,
    };
  },
  computed: {
    standardPlatforms() {
      return this.platforms.filter((platform) => platform.name !== "facebook");
    },
    facebookPlatform() {
      return this.platforms.find((platform) => platform.name === "facebook") || null;
    },
    isFacebookConnected() {
      const facebookPlatform = this.platforms.find((platform) => platform.name === "facebook");
      return !!(facebookPlatform && facebookPlatform.connected);
    },
    facebookPageIdSavedNormalized() {
      return String(this.facebookPageId || "").trim();
    },
    facebookPageIdDraftNormalized() {
      return String(this.facebookPageIdDraft || "").trim();
    },
    facebookPageIdHasUnsavedChanges() {
      return this.facebookPageIdDraftNormalized !== this.facebookPageIdSavedNormalized;
    },
    facebookPageIdSubmitDisabled() {
      if (this.facebookPageIdSaving) return true;
      return !this.facebookPageIdHasUnsavedChanges;
    },
    facebookPageSelectDisabled() {
      if (!this.isFacebookConnected) return true;
      return this.facebookPagesLoading || !this.facebookPageOptions.length;
    },
    facebookPageSaveDisabled() {
      if (!this.isFacebookConnected) return true;
      return this.facebookPageIdSubmitDisabled;
    },
    facebookPageSelectPlaceholder() {
      if (!this.isFacebookConnected) {
        return this.tt("userProfile.facebookPageUnifiedPlaceholder", "Conecta y elige página");
      }
      return this.tt("userProfile.facebookPageIdPlaceholder", "Elige página");
    },
    facebookPageSelectAriaLabel() {
      return this.tt("userProfile.facebookPageUnifiedPlaceholder", "Conecta y elige página");
    },
    facebookPageIdSubmitLabel() {
      if (this.facebookPageIdSaving) {
        return this.tt("userProfile.saving", "Guardando…");
      }
      if (!this.facebookPageIdSavedNormalized) {
        return this.tt("userProfile.savePageId", "Guardar");
      }
      return this.tt("userProfile.updatePageId", "Actualizar");
    },
    facebookPageOptions() {
      const options = [];
      const seenIds = new Set();

      (this.facebookPages || []).forEach((page) => {
        const pageId = String(page?.id || "").trim();
        if (!pageId || seenIds.has(pageId)) return;
        const pageName = String(page?.name || "").trim() || pageId;
        options.push({
          id: pageId,
          name: pageName,
        });
        seenIds.add(pageId);
      });

      const selectedPageId = this.facebookPageIdDraftNormalized;
      if (selectedPageId && !seenIds.has(selectedPageId)) {
        options.unshift({
          id: selectedPageId,
          name: this.tt("userProfile.facebookPageUnknownName", "Página guardada"),
        });
      }

      return options;
    },
  },
  async mounted() {
    let hasError = false;
    try {
      await this.fetchConnections({ throwOnError: true });
    } catch (error) {
      hasError = true;
    } finally {
      this.reportInitialConnectionsLoad(hasError);
    }
    // Refresh connections when window regains focus (user returns from connection page)
    window.addEventListener("focus", this.handleWindowFocus);
  },
  beforeDestroy() {
    // Clean up event listener
    window.removeEventListener("focus", this.handleWindowFocus);
  },
  methods: {
    handleWindowFocus() {
      this.fetchConnections({ forceRefresh: true });
    },
    reportInitialConnectionsLoad(hasError = false) {
      if (this.initialConnectionsLoadReported) return;
      this.initialConnectionsLoadReported = true;
      this.$emit("initial-load-complete", { hasError: !!hasError });
    },
    tt(key, fallback) {
      return typeof this.$t === "function" ? this.$t(key) : fallback;
    },
    formatHandle(username) {
      if (!username || typeof username !== "string") return username || "";
      const trimmed = username.trim();
      return trimmed.startsWith("@") ? trimmed : `@${trimmed}`;
    },
    platformSecondaryText(platform) {
      if (!platform || platform.comingSoon || !platform.connected || !platform.username) {
        return "";
      }
      return this.formatHandle(platform.username);
    },
    platformStatusLabel(platform) {
      if (!platform) return "";
      if (platform.comingSoon) {
        return this.tt("common.comingSoon", "Próximamente");
      }
      if (platform.connected) {
        return this.tt("userProfile.connected", "Conectado");
      }
      return this.tt("userProfile.connect", "Conectar");
    },
    platformStatusBadgeClass(platform) {
      if (!platform) return "social-status-badge--connect";
      if (platform.comingSoon) return "social-status-badge--soon";
      if (platform.connected) return "social-status-badge--connected";
      return "social-status-badge--connect";
    },
    handleFacebookLeftClick(platform) {
      if (!platform || platform.comingSoon) return;
      this.handleCardClick(platform);
    },
    onFacebookPageDraftChange() {
      if (!this.isFacebookConnected || this.facebookPageIdSaving || !this.facebookPageIdHasUnsavedChanges) {
        return;
      }
      this.saveFacebookPageId();
    },
    async resolveSocialConnectSubscriptionGate(existingSession = null) {
      if (isImpersonating()) {
        this.scheduleSubscriptionHasActivePlan = true;
        return true;
      }
      try {
        const currentUserStore = useCurrentUserStore();
        await currentUserStore.ensureSessionReady(existingSession, { syncCommerce: true, forceCommerce: true });
        const resolvedRole = String(currentUserStore.useRole || "").trim().toLowerCase();
        if (resolvedRole === "admin") {
          this.scheduleSubscriptionHasActivePlan = true;
          return true;
        }
        this.scheduleSubscriptionHasActivePlan = currentUserStore.canConnectSocials;
        return this.scheduleSubscriptionHasActivePlan;
      } catch (error) {
        console.warn("No se pudo validar la suscripción antes de abrir conexión social:", error);
        this.scheduleSubscriptionHasActivePlan = false;
        return false;
      }
    },
    openScheduleSubscriptionPromptModal() {
      this.showScheduleSubscriptionPromptModal = true;
    },
    closeScheduleSubscriptionPromptModal() {
      this.showScheduleSubscriptionPromptModal = false;
      this.scheduleSubscriptionHasActivePlan = false;
    },
    async openSubscriptionFromSchedulePrompt() {
      this.closeScheduleSubscriptionPromptModal();
      if (!this.$router) return;
      try {
        await this.$router.push({ name: "billing" });
      } catch (error) {
        const message = String(error?.message || "");
        if (!message.includes("NavigationDuplicated")) {
          this.$notify({
            message: `${this.tt("common.error", "Error")}: ${message || this.tt("schedulePost.errorOpenSettings", "No se pudo abrir configuración.")}`,
            type: "danger",
          });
        }
      }
    },
    normalizeFacebookPage(rawPage) {
      if (!rawPage || typeof rawPage !== "object") return null;
      const id = String(rawPage.id || "").trim();
      if (!id) return null;
      const name = String(rawPage.name || "").trim() || id;
      return { id, name };
    },
    async fetchFacebookPages(existingSession = null) {
      if (this.facebookPagesLoading) return;

      const session = existingSession || (await supabase.auth.getSession())?.data?.session;
      if (!session) return;

      this.facebookPagesLoading = true;
      this.facebookPagesLoadError = "";
      try {
        const apiUrl = process.env.VUE_APP_API_URL || "http://localhost:8000";
        const response = await fetch(`${apiUrl}/momo/social/facebook-pages`, {
          method: "GET",
          headers: buildAuthHeaders(session),
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => null);
          const detail = errData?.detail;
          const errorMessage = typeof detail === "string"
            ? detail
            : detail?.code || response.statusText || "No se pudieron cargar las páginas de Facebook";
          throw new Error(errorMessage);
        }

        const payload = await response.json();
        const rawPages = Array.isArray(payload?.pages) ? payload.pages : [];
        this.facebookPages = rawPages
          .map((page) => this.normalizeFacebookPage(page))
          .filter((page) => !!page);
      } catch (error) {
        console.error("Error fetching facebook pages:", error);
        this.facebookPages = [];
        this.facebookPagesLoadError = String(error?.message || "unknown_error");
      } finally {
        this.facebookPagesLoading = false;
      }
    },
    async fetchConnections({ throwOnError = false, forceRefresh = false } = {}) {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        if (throwOnError) {
          throw new Error(this.tt("common.noActiveSession", "No active session."));
        }
        return;
      }

      try {
        const socialProfileStore = useSocialProfileStore();
        const facebookPageIdStore = useFacebookPageIdStore();
        await Promise.all([
          socialProfileStore.fetchProfile(session, { force: forceRefresh }),
          facebookPageIdStore.fetchPageId(session, { force: forceRefresh }),
        ]);
        const usernamesByPlatform = socialProfileStore.usernamesByPlatform || {};

        this.platforms.forEach((p) => {
          if (p.comingSoon) return;
          p.username = usernamesByPlatform[p.name] || "";
          p.connected = !!p.username;
        });

        const pageId = String(facebookPageIdStore.pageId || "").trim();
        this.facebookPageId = pageId;
        this.facebookPageIdDraft = pageId;

        const facebookPlatform = this.platforms.find((platform) => platform.name === "facebook");
        if (facebookPlatform && facebookPlatform.connected) {
          if (!this.facebookPagesRequestedForCurrentConnection) {
            this.facebookPagesRequestedForCurrentConnection = true;
            await this.fetchFacebookPages(session);
          }
        } else {
          this.facebookPages = [];
          this.facebookPagesLoadError = "";
          this.facebookPagesRequestedForCurrentConnection = false;
        }
      } catch (error) {
        console.error("Error fetching connections:", error);
        this.platforms.forEach((p) => {
          if (p.comingSoon) return;
          p.connected = false;
          p.username = "";
        });
        this.facebookPageId = "";
        this.facebookPageIdDraft = "";
        this.facebookPages = [];
        this.facebookPagesLoadError = "";
        if (throwOnError) {
          throw error;
        }
      }
    },
    async handleCardClick(platform) {
      if (!platform || platform.comingSoon || this.loading) return;
      const canOpenSocialConnect = await this.resolveSocialConnectSubscriptionGate();
      if (!canOpenSocialConnect) {
        this.openScheduleSubscriptionPromptModal();
        return;
      }
      this.openConnectionPage();
    },
    async openConnectionPage(platformsToConnect = this.connectablePlatforms) {
      this.loading = true;
      try {
         const { data: { session } } = await supabase.auth.getSession();
         if (!session) return;
         const canOpenSocialConnect = await this.resolveSocialConnectSubscriptionGate(session);
         if (!canOpenSocialConnect) {
           this.openScheduleSubscriptionPromptModal();
           return;
         }

         const apiUrl = process.env.VUE_APP_API_URL || "http://localhost:8000";
         const redirectUrl = `${window.location.origin}/user-profile`;

         const response = await fetch(`${apiUrl}/momo/social/connect`, {
             method: 'POST',
             headers: buildAuthHeaders(session),
             body: JSON.stringify({
                 redirect_url: redirectUrl,
                 redirect_button_text: this.tt("userProfile.returnToProfile", "Volver al perfil"),
                 platforms: Array.isArray(platformsToConnect) ? platformsToConnect : this.connectablePlatforms,
                 show_calendar: true
             })
         });

         if (!response.ok) {
             const errData = await response.json().catch(() => null);
             throw new Error(errData?.detail || response.statusText || "Failed to generate connection link");
         }

         const data = await response.json();
         // Open in new tab
         window.open(data.url, '_blank');

         this.$notify({
            message: this.tt("userProfile.connectionPageOpened", "Se abrió la página de conexión"),
            type: "info",
         });
         
         // Refresh connections after a delay (in case user connects quickly)
         setTimeout(() => {
          this.fetchConnections({ forceRefresh: true });
         }, 2000);

      } catch (error) {
          console.error(error);
          this.$notify({
            message: `${this.tt("common.error", "Error")}: ${error.message}`,
            type: "danger",
          });
      } finally {
          this.loading = false;
      }
    },
    async saveFacebookPageId() {
      if (!this.isFacebookConnected || !this.facebookPageIdHasUnsavedChanges || this.facebookPageIdSaving) {
        return;
      }
      const pageId = String(this.facebookPageIdDraft || "").trim();
      if (!pageId) {
        this.$notify({
          message: this.tt("userProfile.facebookPageIdRequired", "El ID de la página es obligatorio"),
          type: "danger",
        });
        return;
      }

      this.facebookPageIdSaving = true;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const facebookPageIdStore = useFacebookPageIdStore();
        const savedPageId = await facebookPageIdStore.updatePageId(pageId, session);
        this.facebookPageId = savedPageId;
        this.facebookPageIdDraft = savedPageId;

        this.$notify({
          message: this.tt("userProfile.facebookPageIdSaved", "Facebook Page ID actualizado correctamente"),
          type: "success",
        });
      } catch (error) {
        console.error("Error saving facebook page id:", error);
        this.$notify({
          message: `${this.tt("common.error", "Error")}: ${error.message}`,
          type: "danger",
        });
      } finally {
        this.facebookPageIdSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.social-connections-list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.social-platform-card {
  background: var(--momo-bg-card-glass);
  border: 1px solid var(--momo-border-color);
  border-radius: 12px;
  box-shadow: var(--momo-shadow-card);
  color: var(--momo-text-primary);
  overflow: hidden;
  cursor: pointer;
  transition: var(--momo-transition);
  margin-bottom: 0;
}

.social-platform-card-body {
  padding: 1rem 1.25rem;
  background: transparent;
}

.social-platform-card:hover {
  transform: translateY(-2px);
  border-color: var(--momo-border-hover);
  box-shadow: var(--momo-shadow-card-hover);
}

.social-platform-card-connected {
  border-color: rgba(34, 197, 94, 0.26);
  background:
    linear-gradient(0deg, rgba(34, 197, 94, 0.06), rgba(34, 197, 94, 0.06)),
    var(--momo-bg-card-glass);
  box-shadow:
    var(--momo-shadow-card),
    0 0 0 1px rgba(34, 197, 94, 0.08),
    0 8px 24px rgba(34, 197, 94, 0.08);
}

.social-platform-card-connected:hover {
  border-color: rgba(34, 197, 94, 0.36);
  box-shadow:
    var(--momo-shadow-card-hover),
    0 0 0 1px rgba(34, 197, 94, 0.12),
    0 10px 28px rgba(34, 197, 94, 0.12);
}

.social-platform-card:focus-visible {
  outline: 2px solid var(--momo-purple-light);
  outline-offset: 2px;
}

.social-platform-card-static {
  cursor: default;
}

.social-platform-card-static:hover {
  transform: none;
  border-color: var(--momo-border-color);
  box-shadow: var(--momo-shadow-card);
}

.social-platform-card-disabled {
  cursor: not-allowed;
  opacity: 0.82;
}

.social-platform-card-disabled:hover {
  transform: none;
  border-color: var(--momo-border-color);
  box-shadow: var(--momo-shadow-card);
}

.facebook-connection-panel {
  display: grid;
  grid-template-columns: minmax(155px, 0.7fr) minmax(260px, 1.3fr);
  align-items: center;
  gap: 1rem;
  padding: 0.05rem 0;
}

.facebook-connection-identity {
  min-height: 54px;
  padding: 0 0.15rem;
}

.facebook-page-settings {
  min-width: 0;
  padding-left: 1rem;
  border-left: 1px solid rgba(148, 163, 184, 0.12);
}

.facebook-page-settings-label {
  display: block;
  margin: 0 0 0.38rem;
  color: var(--clipr-text-secondary);
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1.3;
}

.facebook-page-settings-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.social-platform-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.social-platform-left {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 0;
}

.social-logo {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  object-fit: contain;
  display: block;
}

.social-platform-info {
  flex: 1;
  min-width: 0;
}

.social-platform-name {
  font-weight: 600;
  font-size: 1.1rem;
  color: var(--clipr-text-primary);
  margin-bottom: 0.15rem;
}

.social-platform-handle {
  font-size: 0.875rem;
  color: var(--clipr-text-secondary);
}

.social-platform-right {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  flex-shrink: 0;
}

.social-platform-status {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: var(--clipr-text-secondary);
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-dot-connected {
  background-color: #22c55e;
}

.status-dot-disconnected {
  background-color: var(--clipr-text-secondary);
  opacity: 0.6;
}

.status-dot-soon {
  background-color: #f59e0b;
}

.status-text {
  color: var(--clipr-text-secondary);
}

.social-platform-actions {
  flex-shrink: 0;
}

.social-platform-actions .btn-primary {
  background: var(--clipr-purple-gradient);
  border: none;
  box-shadow: 0 4px 14px var(--clipr-purple-glow);
  border-radius: 8px;
  font-weight: 500;
}

.social-platform-actions .btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #a855f7 0%, #d946ef 100%);
  box-shadow: var(--clipr-shadow-button-hover);
}

.facebook-page-id-section-inner {
  position: relative;
  padding: 1rem 1rem 0.95rem;
  border-radius: 14px;
  border: 1px solid rgba(24, 119, 242, 0.35);
  background: linear-gradient(
    145deg,
    rgba(24, 119, 242, 0.12) 0%,
    rgba(15, 23, 42, 0.35) 55%,
    rgba(99, 102, 241, 0.08) 100%
  );
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.2);
}

.facebook-page-id-heading {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  margin-bottom: 0.85rem;
}

.facebook-page-id-icon {
  flex-shrink: 0;
  width: 2.25rem;
  height: 2.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  color: #fff;
  border-radius: 10px;
  background: linear-gradient(135deg, #1877f2 0%, #0d5dbf 100%);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 2px 8px rgba(24, 119, 242, 0.35);
}

.facebook-page-id-heading-text {
  min-width: 0;
  flex: 1;
}

.facebook-page-id-title {
  display: block;
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--clipr-text-primary);
  line-height: 1.3;
  margin-bottom: 0.35rem;
}

.facebook-page-id-label {
  display: block;
  margin: 0 0 0.4rem;
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--clipr-text-primary);
}

.facebook-page-id-controls {
  display: flex;
  align-items: stretch;
  gap: 0.55rem;
  flex-wrap: wrap;
}

.facebook-page-id-input {
  flex: 1 1 200px;
  min-width: 0;
  max-width: 100%;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(15, 23, 42, 0.45);
  color: var(--clipr-text-primary);
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.facebook-page-id-input::placeholder {
  color: rgba(148, 163, 184, 0.75);
}

.facebook-page-id-input:focus {
  outline: none;
  border-color: rgba(24, 119, 242, 0.65);
  box-shadow: 0 0 0 3px rgba(24, 119, 242, 0.2);
}

.facebook-page-id-save {
  flex-shrink: 0;
  align-self: center;
  border-radius: 10px;
  padding-left: 1rem;
  padding-right: 1rem;
  font-weight: 600;
}

.facebook-page-id-feedback {
  margin-top: 0.45rem;
  font-size: 0.75rem;
  color: var(--clipr-text-secondary);
}

.facebook-page-id-feedback-error {
  color: #f87171;
}

@media (max-width: 991px) {
  .social-platform-card-body {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .facebook-page-id-section-inner {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}

@media (max-width: 575px) {
  .social-connections-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.55rem;
    align-items: start;
  }

  .social-platform-card {
    border-radius: 10px;
    min-width: 0;
  }

  .social-platform-card-body {
    padding: 0.72rem 1rem;
  }

  .social-platform-row {
    gap: 0.5rem;
    flex-direction: column;
    align-items: flex-start;
    flex-wrap: nowrap;
  }

  .social-platform-left {
    width: 100%;
    gap: 0.55rem;
    align-items: flex-start;
  }

  .social-logo {
    width: 34px;
    height: 34px;
  }

  .social-platform-name {
    font-size: 0.9rem;
    line-height: 1.2;
    margin-bottom: 0.05rem;
  }

  .social-platform-handle {
    font-size: 0.72rem;
    line-height: 1.3;
    word-break: break-word;
  }

  .social-platform-right {
    width: auto;
    justify-content: flex-start;
    align-items: center;
  }

  .social-platform-status {
    font-size: 0.7rem;
    gap: 0.3rem;
  }

  .status-text {
    line-height: 1.2;
  }

  .facebook-page-id-card {
    grid-column: auto;
  }

  .facebook-page-id-section-inner {
    padding: 0.7rem 1rem;
  }

  .facebook-page-id-heading {
    gap: 0.5rem;
    margin-bottom: 0.6rem;
  }

  .facebook-page-id-icon {
    width: 1.8rem;
    height: 1.8rem;
    border-radius: 8px;
  }

  .facebook-page-id-title {
    font-size: 0.82rem;
    margin-bottom: 0.2rem;
  }

  .facebook-page-id-label {
    font-size: 0.72rem;
  }

  .facebook-page-id-controls {
    gap: 0.45rem;
  }

  .facebook-page-id-input {
    flex-basis: 100%;
    font-size: 0.76rem;
    padding: 0.45rem 0.6rem;
  }

  .facebook-page-id-save {
    width: 100%;
    font-size: 0.72rem;
    padding: 0.45rem 0.75rem;
  }

  .facebook-connection-panel {
    grid-template-columns: 1fr;
    gap: 0.6rem;
    padding: 0;
  }

  .facebook-page-settings {
    padding: 0.7rem 0 0;
    border-top: 1px solid rgba(148, 163, 184, 0.15);
    border-left: none;
  }

  .facebook-page-settings-controls {
    align-items: stretch;
    flex-direction: column;
  }
}

.social-connections-list--compact {
  gap: 0.35rem;
}

/* ===== Unified: flat rows embedded in the profile card ===== */

.social-connections-list--unified {
  gap: 0;
}

.social-connections-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 1.25rem 0.55rem;
}

.social-connections-header-title {
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: var(--clipr-text-primary);
  text-align: center;
}

.social-connections-list--paired-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.social-connections-list--paired-grid .social-connections-header {
  grid-column: 1 / -1;
}

.social-connections-list--unified .social-platform-card {
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
  transition: background 0.15s ease;
}

.social-connections-list--unified .social-platform-card:hover {
  transform: none;
  background: rgba(255, 255, 255, 0.028);
  border: none;
  box-shadow: none;
}

.social-connections-list--unified .social-platform-card-connected:hover {
  background: rgba(34, 197, 94, 0.045);
}

.social-connections-list--unified .social-platform-card:focus-visible {
  outline: 2px solid var(--clipr-purple, #a855f7);
  outline-offset: -2px;
  border-radius: 10px;
}

.social-connections-list--unified .social-platform-card-body {
  padding: 0.7rem 1.25rem;
}

.social-connections-list--unified .social-platform-row {
  align-items: center;
  gap: 0.75rem;
  flex-wrap: nowrap;
  min-height: 44px;
}

.social-connections-list--unified .social-platform-left {
  gap: 0.75rem;
}

.social-connections-list--unified .social-logo {
  width: 34px;
  height: 34px;
}

.social-connections-list--unified .social-platform-name {
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0;
}

.social-connections-list--unified .social-platform-handle {
  font-size: 0.74rem;
  margin-top: 0.08rem;
}

.social-connections-list--unified .social-platform-right {
  align-items: center;
}

/* Hairlines inside the 2x2 grid (the header is the first grid child) */
.social-connections-list--paired-grid .social-platform-card:nth-child(2) .social-platform-card-body,
.social-connections-list--paired-grid .social-platform-card:nth-child(4) .social-platform-card-body {
  border-right: 1px solid rgba(148, 163, 184, 0.09);
}

.social-connections-list--paired-grid .social-platform-card:nth-child(2) .social-platform-card-body,
.social-connections-list--paired-grid .social-platform-card:nth-child(3) .social-platform-card-body {
  border-bottom: 1px solid rgba(148, 163, 184, 0.09);
}

.social-connections-list--paired-grid .social-platform-card--facebook-row .social-platform-card-body {
  border-top: 1px solid rgba(148, 163, 184, 0.09);
  border-right: 1px solid rgba(148, 163, 184, 0.09);
  padding-top: 0.75rem;
  padding-bottom: 0.85rem;
}

.social-connections-list--paired-grid .social-platform-card--facebook-page .social-platform-card-body {
  border-top: 1px solid rgba(148, 163, 184, 0.09);
  padding-top: 0.75rem;
  padding-bottom: 0.85rem;
}

.social-platform-card--facebook-page.social-platform-card-static {
  cursor: default;
}

.social-platform-card--facebook-page.social-platform-card-static:hover {
  background: transparent;
}

.social-platform-card-body--facebook-page {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  gap: 0.35rem;
  width: 100%;
  min-height: 40px;
}

.facebook-page-select-field {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  width: 100%;
  min-width: 0;
}

.facebook-page-select-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: block;
  border-radius: 50%;
}

.facebook-page-id-select--solo {
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
  min-height: 26px;
  padding: 0.2rem 1.35rem 0.2rem 0.45rem;
  font-size: 0.66rem;
  font-weight: 500;
  line-height: 1.25;
  border-radius: 7px;
  cursor: pointer;
}

.facebook-page-id-select--solo:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.facebook-page-id-feedback--solo {
  font-size: 0.68rem;
  color: var(--clipr-text-secondary);
}

.social-connections-list--unified:not(.social-connections-list--paired-grid)
  .social-platform-card:not(:last-child)
  .social-platform-card-body {
  border-bottom: 1px solid rgba(148, 163, 184, 0.09);
}

.social-connections-list--paired-grid .social-platform-card-body {
  min-height: 100%;
}

/* ===== Status badges ===== */

.social-status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 5.5rem;
  padding: 0.28rem 0.65rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.social-connections-list--unified .social-status-badge {
  min-width: auto;
  padding: 0.24rem 0.6rem;
}

.social-status-badge--connected {
  color: #4ade80;
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.26);
}

.social-status-badge--connected::before {
  content: "";
  width: 6px;
  height: 6px;
  margin-right: 0.35rem;
  border-radius: 50%;
  background: #4ade80;
  box-shadow: 0 0 6px rgba(74, 222, 128, 0.65);
}

.social-status-badge--connect {
  color: #d8b4fe;
  background: rgba(168, 85, 247, 0.1);
  border: 1px solid rgba(168, 85, 247, 0.26);
}

.social-status-badge--connect::before {
  content: "+";
  margin-right: 0.3rem;
  font-weight: 700;
  line-height: 1;
}

.social-platform-card:not(.social-platform-card-disabled):hover .social-status-badge--connect {
  color: #ede9fe;
  background: rgba(168, 85, 247, 0.22);
  border-color: rgba(168, 85, 247, 0.45);
}

.social-status-badge--soon {
  color: #fbbf24;
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.28);
}

.social-status-badge--inline {
  margin-top: 0.2rem;
  min-width: auto;
  align-self: flex-start;
}

/* ===== Compact tweaks ===== */

.social-connections-list--compact .social-platform-card:hover {
  transform: none;
}

/* ===== Facebook row ===== */

.social-platform-card--facebook-row.social-platform-card-static {
  cursor: default;
}

.social-platform-row--facebook {
  align-items: center;
  gap: 0.85rem;
}

.facebook-connection-panel .social-platform-right--facebook {
  flex: 1;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.social-platform-left--clickable {
  cursor: pointer;
  border-radius: 10px;
}

.facebook-page-id-input--inline {
  flex: 1 1 180px;
  min-width: 0;
  max-width: 260px;
  min-height: 36px;
  padding: 0.45rem 0.65rem;
  font-size: 0.8rem;
  border-radius: 10px;
}

.facebook-page-id-save--inline {
  flex-shrink: 0;
  min-height: 36px;
  border-radius: 9px;
  padding-left: 0.85rem;
  padding-right: 0.85rem;
  font-size: 0.8125rem;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
}

.facebook-page-id-input--inline:disabled,
.facebook-page-id-save--inline:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.facebook-page-id-feedback--inline {
  margin-top: 0.45rem;
  font-size: 0.72rem;
}

.social-connections-list--unified .social-platform-card--facebook-page .social-platform-card-body {
  padding: 0.7rem 1.25rem;
}

/* ===== Mobile ===== */

@media (max-width: 575px) {
  .social-connections-list--paired-grid {
    grid-template-columns: 1fr;
  }

  .social-connections-header {
    padding: 0.9rem 1rem 0.45rem;
  }

  .social-connections-list--unified .social-platform-card-body {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .social-connections-list--paired-grid .social-platform-card .social-platform-card-body {
    border-right: none;
    border-bottom: none;
  }

  .social-connections-list--paired-grid
    .social-platform-card:not(.social-platform-card--facebook-row):not(.social-platform-card--facebook-page)
    .social-platform-card-body {
    border-bottom: 1px solid rgba(148, 163, 184, 0.09);
  }

  .social-connections-list--paired-grid .social-platform-card--facebook-row .social-platform-card-body,
  .social-connections-list--paired-grid .social-platform-card--facebook-page .social-platform-card-body {
    border-top: none;
    border-right: none;
  }

  .social-connections-list--unified .social-platform-row {
    flex-direction: row;
    align-items: center;
    flex-wrap: nowrap;
  }

  .social-platform-row--facebook {
    flex-direction: column;
    align-items: stretch;
  }

  .facebook-connection-panel .social-platform-right--facebook {
    justify-content: stretch;
    flex-direction: column;
    align-items: stretch;
  }

  .facebook-page-id-input--inline {
    max-width: none;
    width: 100%;
  }

  .facebook-page-id-save--inline {
    width: 100%;
  }
}
</style>
